// The Passes screen's server state (spec/13, DESIGN.md "Pass row"; F-PRC-01/-02/-04/-05/-06), as
// TanStack Query hooks over the generated client — `packages/contracts/src/routes/passes.ts`:
//
//   GET  /v1/organizations/{o}/passes/products            the pass catalogue        passes:read
//   POST /v1/organizations/{o}/passes/products            make one                  passes:manage
//
// Selling a pass is not here: it is a pass line on a Sell-screen sale (`createSale`, F-POS-01), so it is a sale
// in Orders. The `…/passes/products/{id}/sell` door, which issues a holding with no sale, is no longer used.
//   POST …/passes/products/{id}/grant                     staff grant a holding     passes:grant
//   GET  /v1/organizations/{o}/passes/holdings            holdings                  passes:read
//
// Pass products are the organisation's catalogue; today's count and the grant action name the
// **venue** with the `X-Clavian-Venue` header, because a grant is made there. A new random
// `Idempotency-Key` per grant and create means a double-click posts once.

import type {
  CreatePassProductRequest,
  GrantPassRequest,
  IdempotencyKey,
  PassHolding,
  PassProduct,
  RefundPassRequest,
  UpdatePassProductRequest,
  VoidPassRequest,
} from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const passesKeys = {
  products: (organizationId: string, venueId: string) => ['passes', organizationId, venueId, 'products'] as const,
  holdings: (organizationId: string, playerId: string) => ['passes', organizationId, 'holdings', playerId] as const,
};

/** The `X-Clavian-Venue` header a sale is taken under. */
function venueHeader(venueId: string): { readonly 'X-Clavian-Venue': string } {
  return { 'X-Clavian-Venue': venueId };
}

const newKey = (): IdempotencyKey => crypto.randomUUID() as IdempotencyKey;

/** The organisation's pass products, with today's count at this venue. */
export function usePassProducts(organizationId: string, venueId: string) {
  const api = useApi();
  return useQuery<readonly PassProduct[]>({
    queryKey: passesKeys.products(organizationId, venueId),
    queryFn: async () => (await api.listPassProducts({ path: { organizationId }, headers: venueHeader(venueId) })).data,
  });
}

/** Make a pass product, then re-read the catalogue. */
export function useCreatePassProduct(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePassProductRequest) => api.createPassProduct({ path: { organizationId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: passesKeys.products(organizationId, venueId) }),
  });
}

/**
 * Edit a pass product (F-PRC-01, `passes:manage`); a term change mints a new version server-side,
 * while `active`/`sortOrder`/`accentToken` change in place. Only the fields sent change. Re-reads.
 */
export function useUpdatePassProduct(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, body }: { productId: string; body: UpdatePassProductRequest }) => api.updatePassProduct({ path: { organizationId, productId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: passesKeys.products(organizationId, venueId) }),
  });
}

/** Staff grant a pass to a player at this venue, with a reason. */
export function useGrantPass(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<PassHolding, unknown, { productId: string; body: GrantPassRequest }>({
    mutationFn: ({ productId, body }) =>
      api.grantPass({ path: { organizationId, productId }, headerParams: { 'idempotency-key': newKey() }, headers: venueHeader(venueId), body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: passesKeys.products(organizationId, venueId) }),
  });
}

/** A player's pass holdings at this organisation, newest first (F-PRC-02, `passes:read`). Read only when a player is chosen. */
export function usePassHoldings(organizationId: string, playerId: string | undefined) {
  const api = useApi();
  return useQuery<readonly PassHolding[]>({
    queryKey: passesKeys.holdings(organizationId, playerId ?? ''),
    enabled: playerId !== undefined,
    queryFn: async () => (await api.listPassHoldings({ path: { organizationId }, query: { playerId: playerId as string, limit: 50 } })).data,
  });
}

/** Refund a pass holding to a tender, with an optional reason (F-PRC-04, `passes:refund`). Re-reads the player's holdings. */
export function useRefundPassHolding(organizationId: string, playerId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<PassHolding, unknown, { holdingId: string; body: RefundPassRequest }>({
    mutationFn: ({ holdingId, body }) => api.refundPassHolding({ path: { organizationId, holdingId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: passesKeys.holdings(organizationId, playerId) }),
  });
}

/** Void a pass holding's unused seconds, with a reason (F-PRC-05, `passes:void`). Re-reads the player's holdings. */
export function useVoidPassHolding(organizationId: string, playerId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<PassHolding, unknown, { holdingId: string; body: VoidPassRequest }>({
    mutationFn: ({ holdingId, body }) => api.voidPassHolding({ path: { organizationId, holdingId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: passesKeys.holdings(organizationId, playerId) }),
  });
}
